import React, { useState, useEffect } from 'react';
import { AppTheme, ProductNew, ContractorNew, ContractNew, Warehouse, ProductCategory } from '../types';

interface ProductNewFormProps {
  theme: AppTheme;
  warehouses: Warehouse[];
  contractors: ContractorNew[];
  contracts: ContractNew[];
  categories: ProductCategory[];
  onSubmit: (data: Omit<ProductNew, 'id' | 'created_at' | 'is_deleted'>) => void;
  onCancel: () => void;
  initialData?: ProductNew;
}

export const ProductNewForm: React.FC<ProductNewFormProps> = ({
  theme,
  warehouses,
  contractors,
  contracts,
  categories,
  onSubmit,
  onCancel,
  initialData
}) => {
  const [formData, setFormData] = useState({
    warehouse_id: '',
    contractor_id: '',
    contract_id: '',
    category_id: '',
    is_service: false,
    code: '',
    name: '',
    description: '',
    count_value: 0,
    count_metric: ''
  });

  useEffect(() => {
    if (initialData) {
      setFormData({
        warehouse_id: initialData.warehouse_id || '',
        contractor_id: initialData.contractor_id || '',
        contract_id: initialData.contract_id || '',
        category_id: initialData.category_id || '',
        is_service: initialData.is_service || false,
        code: initialData.code || '',
        name: initialData.name || '',
        description: initialData.description || '',
        count_value: initialData.count_value || 0,
        count_metric: initialData.count_metric || ''
      });
    }
  }, [initialData]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
  };

  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '8px',
    border: `1px solid ${theme.colors.border}`,
    borderRadius: '4px',
    backgroundColor: theme.colors.background,
    color: theme.colors.text,
    fontSize: '14px',
  };

  const labelStyle: React.CSSProperties = {
    display: 'block',
    marginBottom: '4px',
    fontWeight: 'bold',
    color: theme.colors.text,
  };

  const buttonStyle: React.CSSProperties = {
    padding: '8px 16px',
    margin: '0 4px',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '14px',
  };

  return (
    <form onSubmit={handleSubmit} style={{ padding: '16px' }}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
        <div>
          <label style={labelStyle}>Склад:</label>
          <select
            value={formData.warehouse_id}
            onChange={(e) => setFormData({ ...formData, warehouse_id: e.target.value })}
            style={inputStyle}
            required
          >
            <option value="">Выберите склад</option>
            {warehouses.map((warehouse) => (
              <option key={warehouse.id} value={warehouse.id}>
                {warehouse.name}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label style={labelStyle}>Контрагент:</label>
          <select
            value={formData.contractor_id}
            onChange={(e) => setFormData({ ...formData, contractor_id: e.target.value })}
            style={inputStyle}
            required
          >
            <option value="">Выберите контрагента</option>
            {contractors.map((contractor) => (
              <option key={contractor.id} value={contractor.id}>
                {contractor.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
        <div>
          <label style={labelStyle}>Контракт:</label>
          <select
            value={formData.contract_id}
            onChange={(e) => setFormData({ ...formData, contract_id: e.target.value })}
            style={inputStyle}
            required
          >
            <option value="">Выберите контракт</option>
            {contracts.map((contract) => (
              <option key={contract.id} value={contract.id}>
                Контракт от {new Date(contract.sign_date).toLocaleDateString()} - {contract.amount}₽
              </option>
            ))}
          </select>
        </div>

        <div>
          <label style={labelStyle}>Категория:</label>
          <select
            value={formData.category_id}
            onChange={(e) => setFormData({ ...formData, category_id: e.target.value })}
            style={inputStyle}
            required
          >
            <option value="">Выберите категорию</option>
            {categories.map((category) => (
              <option key={category.id} value={category.id}>
                {category.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div style={{ marginBottom: '16px' }}>
        <label style={{ ...labelStyle, display: 'flex', alignItems: 'center' }}>
          <input
            type="checkbox"
            checked={formData.is_service}
            onChange={(e) => setFormData({ ...formData, is_service: e.target.checked })}
            style={{ marginRight: '8px' }}
          />
          Это услуга
        </label>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '16px', marginBottom: '16px' }}>
        <div>
          <label style={labelStyle}>Код:</label>
          <input
            type="text"
            value={formData.code}
            onChange={(e) => setFormData({ ...formData, code: e.target.value })}
            style={inputStyle}
            required
          />
        </div>

        <div>
          <label style={labelStyle}>Название:</label>
          <input
            type="text"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            style={inputStyle}
            required
          />
        </div>
      </div>

      <div style={{ marginBottom: '16px' }}>
        <label style={labelStyle}>Описание:</label>
        <textarea
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          style={{ ...inputStyle, minHeight: '80px', resize: 'vertical' }}
        />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
        <div>
          <label style={labelStyle}>Количество:</label>
          <input
            type="number"
            step="0.01"
            value={formData.count_value}
            onChange={(e) => setFormData({ ...formData, count_value: parseFloat(e.target.value) || 0 })}
            style={inputStyle}
            required
          />
        </div>

        <div>
          <label style={labelStyle}>Единица измерения:</label>
          <input
            type="text"
            value={formData.count_metric}
            onChange={(e) => setFormData({ ...formData, count_metric: e.target.value })}
            style={inputStyle}
            placeholder="шт, кг, л, м и т.д."
            required
          />
        </div>
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '20px' }}>
        <button
          type="button"
          onClick={onCancel}
          style={{
            ...buttonStyle,
            backgroundColor: theme.colors.border,
            color: theme.colors.text,
          }}
        >
          Отмена
        </button>
        <button
          type="submit"
          style={{
            ...buttonStyle,
            backgroundColor: theme.colors.primary,
            color: 'white',
          }}
        >
          {initialData ? 'Сохранить' : 'Добавить'}
        </button>
      </div>
    </form>
  );
};